import { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface TourStep {
  target: string;
  title: string;
  titleAr: string;
  description: string;
  descriptionAr: string;
}

const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";
const TRANSITION_MS = 500;

interface GuidedTourProps {
  steps: TourStep[];
  isOpen: boolean;
  onClose: () => void;
  language: string;
  onStepChange?: (stepIndex: number) => void;
}

export const GuidedTour = ({ steps, isOpen, onClose, language, onStepChange }: GuidedTourProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [mounted, setMounted] = useState(false);
  const resizeRef = useRef<ReturnType<typeof setTimeout>>();
  const measureRef = useRef<ReturnType<typeof setTimeout>>();

  const isAr = language === "ar";
  const step = steps[currentStep];
  const pad = 8;

  const positionTooltip = useCallback((rect: { top: number; left: number; width: number; height: number } | null) => {
    const tooltipW = 380;
    const tooltipH = 200;
    const gap = 16;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (!rect) {
      return { top: vh / 2 - tooltipH / 2, left: vw / 2 - tooltipW / 2 };
    }

    if (rect.top + rect.height + gap + tooltipH < vh) {
      return { top: rect.top + rect.height + gap, left: Math.max(gap, Math.min(rect.left, vw - tooltipW - gap)) };
    }
    if (rect.top - gap - tooltipH > 0) {
      return { top: rect.top - gap - tooltipH, left: Math.max(gap, Math.min(rect.left, vw - tooltipW - gap)) };
    }
    if (rect.left + rect.width + gap + tooltipW < vw) {
      return { top: Math.max(gap, rect.top), left: rect.left + rect.width + gap };
    }
    return { top: Math.max(gap, rect.top), left: Math.max(gap, rect.left - tooltipW - gap) };
  }, []);

  const measureAndHighlight = useCallback((stepIndex: number) => {
    const s = steps[stepIndex];
    if (!s) return;

    const el = document.querySelector(`[data-tour="${s.target}"]`);
    if (el) {
      clearTimeout(measureRef.current);
      // Scroll element into view first, then measure after scroll settles
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      measureRef.current = setTimeout(() => {
        const r = el.getBoundingClientRect();
        const rect = { top: r.top - pad, left: r.left - pad, width: r.width + pad * 2, height: r.height + pad * 2 };
        setSpotlightRect(rect);
        setTooltipPos(positionTooltip(rect));
      }, 450);
    } else {
      setSpotlightRect(null);
      setTooltipPos(positionTooltip(null));
    }
  }, [positionTooltip, steps]);

  const highlightStep = useCallback((stepIndex: number) => {
    requestAnimationFrame(() => measureAndHighlight(stepIndex));
    onStepChange?.(stepIndex);
  }, [measureAndHighlight, onStepChange]);

  // Recalculate spotlight position on scroll
  useEffect(() => {
    if (!isOpen) return;
    const handler = () => {
      clearTimeout(measureRef.current);
      measureRef.current = setTimeout(() => {
        const s = steps[currentStep];
        if (!s) return;
        const el = document.querySelector(`[data-tour="${s.target}"]`);
        if (el) {
          const r = el.getBoundingClientRect();
          const rect = { top: r.top - pad, left: r.left - pad, width: r.width + pad * 2, height: r.height + pad * 2 };
          setSpotlightRect(rect);
          setTooltipPos(positionTooltip(rect));
        }
      }, 50);
    };
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('scroll', handler, true);
      clearTimeout(measureRef.current);
    };
  }, [isOpen, currentStep, steps, positionTooltip]);

  // Mount / open
  useEffect(() => {
    if (!isOpen) { setMounted(false); return; }
    setCurrentStep(0);
    const t = setTimeout(() => {
      setMounted(true);
      highlightStep(0);
    }, 50);
    return () => clearTimeout(t);
  }, [isOpen, highlightStep]);

  // Resize recalc
  useEffect(() => {
    if (!isOpen) return;
    const handler = () => {
      clearTimeout(resizeRef.current);
      resizeRef.current = setTimeout(() => measureAndHighlight(currentStep), 200);
    };
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("resize", handler);
      clearTimeout(resizeRef.current);
    };
  }, [isOpen, currentStep, measureAndHighlight]);

  const goNext = useCallback(() => {
    if (currentStep >= steps.length - 1) { onClose(); return; }
    const next = currentStep + 1;
    setCurrentStep(next);
    highlightStep(next);
  }, [currentStep, onClose, highlightStep, steps]);

  const goBack = useCallback(() => {
    if (currentStep <= 0) return;
    const prev = currentStep - 1;
    setCurrentStep(prev);
    highlightStep(prev);
  }, [currentStep, highlightStep]);

  // Keyboard nav
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" || e.key === "ArrowDown") goNext();
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") goBack();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  if (!isOpen || !step) return null;

  const totalSteps = steps.length;
  const progressPct = ((currentStep + 1) / totalSteps) * 100;

  return (
    <div
      className="fixed inset-0 z-[10000]"
      style={{ opacity: mounted ? 1 : 0, transition: `opacity 0.3s ${EASE}`, touchAction: 'none', overscrollBehavior: 'none' }}
      onTouchMove={(e) => e.preventDefault()}
    >
      {/* Backdrop click target */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Spotlight ring with double glow + animated dashed outline */}
      {spotlightRect && (
        <>
          <div
            className="absolute pointer-events-none rounded-2xl"
            style={{
              top: spotlightRect.top,
              left: spotlightRect.left,
              width: spotlightRect.width,
              height: spotlightRect.height,
              boxShadow: `
                0 0 0 9999px hsl(var(--background) / 0.78),
                0 0 0 1.5px hsl(var(--primary) / 0.95),
                0 0 0 6px hsl(var(--primary) / 0.18),
                0 0 40px 6px hsl(var(--primary) / 0.35),
                0 0 80px 12px hsl(var(--primary) / 0.18)
              `,
              transition: `top ${TRANSITION_MS}ms ${EASE}, left ${TRANSITION_MS}ms ${EASE}, width ${TRANSITION_MS}ms ${EASE}, height ${TRANSITION_MS}ms ${EASE}`,
              backdropFilter: 'none',
            }}
          />
        </>
      )}

      {!spotlightRect && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundColor: "hsl(var(--background) / 0.78)" }}
          onClick={onClose}
        />
      )}

      {/* Tour card */}
      <div
        className="absolute w-[400px] max-w-[calc(100vw-32px)] rounded-2xl overflow-hidden"
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
          zIndex: 10001,
          transition: `top ${TRANSITION_MS}ms ${EASE}, left ${TRANSITION_MS}ms ${EASE}`,
          background: 'linear-gradient(135deg, hsl(var(--card)) 0%, hsl(var(--card)) 100%)',
          boxShadow: `
            0 1px 0 0 hsl(var(--primary) / 0.25) inset,
            0 0 0 1px hsl(var(--border) / 0.7),
            0 20px 60px -10px hsl(var(--primary) / 0.35),
            0 30px 80px -20px hsl(0 0% 0% / 0.55)
          `,
        }}
      >
        {/* Top gradient accent bar */}
        <div
          className="h-[3px] w-full"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, hsl(var(--primary)) 30%, hsl(var(--primary) / 0.6) 70%, transparent 100%)',
          }}
        />

        <div className="p-5">
          {/* Header row */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div
                className="h-8 w-8 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  background: 'linear-gradient(135deg, hsl(var(--primary) / 0.18), hsl(var(--primary) / 0.06))',
                  boxShadow: '0 0 0 1px hsl(var(--primary) / 0.25), 0 6px 16px -6px hsl(var(--primary) / 0.5)',
                }}
              >
                <Sparkles className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.14em] text-primary/80 font-semibold">
                  {isAr ? `الخطوة ${currentStep + 1} من ${totalSteps}` : `Step ${currentStep + 1} of ${totalSteps}`}
                </div>
                <h3
                  key={`title-${currentStep}`}
                  className={`text-[15px] font-bold text-foreground leading-tight truncate animate-fade-in ${isAr ? "text-right" : ""}`}
                >
                  {isAr ? step.titleAr : step.title}
                </h3>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-muted/70 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              aria-label={isAr ? "إغلاق" : "Close"}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Description */}
          <p
            key={`desc-${currentStep}`}
            className={`text-sm text-muted-foreground leading-relaxed mb-4 animate-fade-in ${isAr ? "text-right" : ""}`}
          >
            {isAr ? step.descriptionAr : step.description}
          </p>

          {/* Progress bar */}
          <div className="h-1 w-full rounded-full bg-muted/60 overflow-hidden mb-4">
            <div
              className="h-full rounded-full"
              style={{
                width: `${progressPct}%`,
                background: 'linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary) / 0.7))',
                boxShadow: '0 0 12px hsl(var(--primary) / 0.6)',
                transition: `width 0.45s ${EASE}`,
              }}
            />
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors px-1 py-1"
            >
              {isAr ? "تخطي الجولة" : "Skip tour"}
            </button>

            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goBack}
                  className="rounded-xl h-8 px-3 text-xs border-border/70"
                >
                  <ChevronLeft className="h-3.5 w-3.5 mr-0.5" />
                  {isAr ? "السابق" : "Back"}
                </Button>
              )}

              <Button
                size="sm"
                onClick={goNext}
                className="rounded-xl h-8 px-4 text-xs font-semibold text-primary-foreground border-0"
                style={{
                  background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.85))',
                  boxShadow: '0 6px 16px -4px hsl(var(--primary) / 0.55)',
                }}
              >
                {currentStep >= steps.length - 1
                  ? (isAr ? "إنهاء" : "Finish")
                  : (isAr ? "التالي" : "Next")}
                {currentStep < steps.length - 1 && <ChevronRight className="h-3.5 w-3.5 ml-0.5" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
